import { useLayoutEffect, useRef } from "react";
import { addCalendarDays } from "@common/helpers/date";
import { isReducedMotionEnabled } from "@constants";
import type { DensityChartWindow } from "./DensityChart.lib";
import { clampToHistory } from "./DensityChart.lib";
import type { DensityChartBucketUnit } from "./densityBuckets.lib";
import { dayPosition, toBarIndex } from "./densityBuckets.lib";
import { BAR_PITCH_PX } from "./densityViewport.lib";
import { WINDOW_PART_SELECTOR } from "./windowOverlay.lib";

// ds-deviation: Mercury's zoom timing, 500ms, longer than --duration-slow, so the eye can follow the bars changing unit
const SWAP_TIMING: KeyframeAnimationOptions = {
    duration: 500,
    easing: "cubic-bezier(0.2, 0, 0, 1)",
};
// Every unit change is at least a 4x jump in px per day, where Mercury's squeeze is at its most.
const TICK_SQUEEZE = 0.65;

interface BarFrame {
    unit: DensityChartBucketUnit;
    offset: number;
}

interface SwapFrame extends BarFrame {
    width: number;
    startDate: string;
}

interface UseDensitySwapParams extends SwapFrame {
    /** Holds the axis ticks and the window outline. */
    root: HTMLElement | null;
    endDate: string;
    window?: DensityChartWindow;
}

/** px from where today's bar ends at offset 0 to `position`, in bars from the epoch. */
const toX = (position: number, frame: BarFrame, endDate: string) =>
    (position -
        (toBarIndex({ date: endDate, unit: frame.unit }) + 1 - frame.offset)) *
    BAR_PITCH_PX;

// jsdom has no Web Animations API.
const fromShift = (
    element: Element,
    shift: number,
    keyframe?: Keyframe,
): Animation[] =>
    typeof element.animate === "function"
        ? [
              element.animate(
                  [
                      {
                          offset: 0,
                          transform: `translateX(${shift}px)`,
                          ...keyframe,
                      },
                  ],
                  SWAP_TIMING,
              ),
          ]
        : [];

/**
 * When the bars change unit at the same width, eases the window outline,
 * its handles and the axis ticks from where they were drawn to where they
 * are now, the ticks starting part of the way there so far labels don't fly
 * in. A resize or reduced motion swaps at once.
 */
export function useDensitySwap({
    root,
    endDate,
    window,
    ...frame
}: UseDensitySwapParams) {
    const paintedRef = useRef<SwapFrame | null>(null);
    const animationsRef = useRef<Animation[]>([]);
    const { unit, offset, width, startDate } = frame;

    useLayoutEffect(() => {
        const before = paintedRef.current;
        paintedRef.current = { unit, offset, width, startDate };
        if (
            !root ||
            !window ||
            isReducedMotionEnabled ||
            !before ||
            before.unit === unit ||
            before.width !== width ||
            width <= 0 ||
            before.startDate !== startDate
        ) {
            return;
        }
        const after = { unit, offset };
        const dayX = (date: string, barFrame: BarFrame) =>
            toX(dayPosition({ date, unit: barFrame.unit }), barFrame, endDate);
        const { start } = clampToHistory(window, startDate);
        const end = addCalendarDays(window.end, 1);
        const shifts = {
            start: dayX(start, before) - dayX(start, after),
            end: dayX(end, before) - dayX(end, after),
        };

        for (const animation of animationsRef.current) {
            animation.cancel();
        }
        const animations: Animation[] = [];
        for (const part of root.querySelectorAll<HTMLElement>(
            WINDOW_PART_SELECTOR,
        )) {
            const { windowPart } = part.dataset;
            animations.push(
                ...(windowPart === "body"
                    ? fromShift(part, shifts.start, {
                          width: `${dayX(end, before) - dayX(start, before)}px`,
                      })
                    : fromShift(
                          part,
                          windowPart === "start" ? shifts.start : shifts.end,
                      )),
            );
        }
        for (const tick of root.querySelectorAll<HTMLElement>(
            "[data-tick-start]",
        )) {
            const { tickStart = "", tickEnd = "" } = tick.dataset;
            const wasAt =
                (dayX(tickStart, before) +
                    dayX(addCalendarDays(tickEnd, 1), before)) /
                2;
            const isAt = toX(
                toBarIndex({ date: tickStart, unit }) + 0.5,
                after,
                endDate,
            );
            animations.push(
                ...fromShift(tick, (wasAt - isAt) * (1 - TICK_SQUEEZE)),
            );
        }
        animationsRef.current = animations;
    }, [root, endDate, window, unit, offset, width, startDate]);
}
